import { useRef, useLayoutEffect, useState, useMemo, useCallback, useReducer } from "react"
import PropTypes from "prop-types"
import { get, set, map, size, omit, chain } from "lodash"
import { Link, useNavigate } from "react-router-dom"
import moment from "moment"
import { Paper, IconButton, MenuItem, Menu } from "@mui/material"
import { Star, StarBorder } from "@mui/icons-material"
import Tooltip from "components/material/tooltip"
import Table from "components/material/table"
import ResourceName from "./components/resource/resource-name"
import ResourceIcon from "components/resource-icon"
import RecentResources from "./components/recent-resources"
import FilterForm from "./components/filter-form"
import filterReducer from "./components/filter-form/reducer"
import ResourceMenu from "./components/resource-menu"
import { makeMenuItems } from "./helpers"
import { CURRENCY } from "helpers/shapes"
import { Creators as CompositeCreators } from "model/compositeSecurity"

import "./style.scss"

const ROWS_PER_PAGE = 200

const Resources = (props) => {
  const {
    dispatch,
    account,
    recentResources,
    toggleFavorite,
    models,
    resources,
    model,
    intradayAccountEnabled,
    isOpPro,
    accountDefaultCurrencyId,
    supportedCurrencies,
    favoritesScreen,
  } = props
  const containerRef = useRef()
  const recentRef = useRef()
  const [editAnchor, setEditAnchor] = useState(null)
  const [resource, setResource] = useState(null)
  const [filters, filterDispatch] = useReducer(filterReducer, {})

  const navigate = useNavigate()

  useLayoutEffect(() => {
    // Adjust the height of the Table to fit the screen correctly (no scrolling)
    set(containerRef, "current.style.maxHeight", `calc(100% - ${get(recentRef, "current.clientHeight", 0) + 16}px)`)
  })

  const columns = [{
    id: "name",
    name: "Name",
    sortGetter: resource => resource.resourceType === "swap" ? resource.alias : resource.name,
    render: (resource) => <ResourceName {...{
      account,
      resource,
      hasModels: Boolean(size(models)),
      openBasket: () => dispatch(CompositeCreators.openCompositeSecurity(account, { ...resource, assetClass: "swap" })),
    }}
    />,
  },
  {
    id: "description",
    name: "Description",
  },
  {
    id: "lastUpdated",
    name: "Last updated",
    formatter: value => value ? moment(value).fromNow() : "",
    // This is so we can order by lastUpdated descending even if there are undefined values
    sortGetter: resource => resource.lastUpdated ? moment(resource.lastUpdated).format("X") : 0,
    defaultSort: "desc",
    type: "datetime",
  },
  {
    id: "type",
    name: "Type",
    render: ({ resourceType }) => <ResourceIcon type={resourceType} />,
  },
  {
    id: "isFavorite",
    name: "Favorite",
    render: ({ id, resourceType, isFavorite, ticker }) => <IconButton
      onClick={(ev) => {
        ev.stopPropagation()
        toggleFavorite(ticker || id, resourceType, !isFavorite)
      }}
      size="large"
    >
      {isFavorite ? <Star className="active" /> : <StarBorder />}
    </IconButton>,
    sortGetter: resource => Boolean(resource.isFavorite),
    type: "boolean",
  },
  {
    id: "menu",
    name: "",
    render: (resource) => <ResourceMenu {...{ account, resource, isOpPro, setEditAnchor, setResource }} />,
  }]

  const makeOnClick = useCallback((action) => () => {
    setEditAnchor(null)
    if (!action) { return }
    action(dispatch, navigate)
  }, [dispatch, navigate])

  const menuItems = useMemo(() => map(makeMenuItems(account, resource, isOpPro, { models, intradayAccountEnabled, supportedCurrencies, accountDefaultCurrencyId }), ({ id, label, action, to, className, disabledText }) => {
    if (disabledText) {
      return <Tooltip title={disabledText}>
        {/* the span is to allow the tooltip to work on a disabled button */}
        <span><MenuItem disabled key={id} className={className}>{label}</MenuItem></span>
      </Tooltip>
    }
    if (to) {
      return <MenuItem key={id} className={className} component={Link} to={to}>{label}</MenuItem>
    }
    return <MenuItem key={id} className={className} onClick={makeOnClick(action)}>{label}</MenuItem>
  }), [account, resource, isOpPro, models, intradayAccountEnabled, supportedCurrencies, accountDefaultCurrencyId, makeOnClick])

  // The table component only needs an array of conditions to apply
  const mappedFilters = chain(filters)
    .map(filter => get(filter, "condition"))
    .compact()
    .value()

  const hasModels = Boolean(size(models))

  const openBasket = useCallback(function openBasket(resource) {
    dispatch(CompositeCreators.openCompositeSecurity(account, { ...resource, assetClass: "swap" }))
  }, [dispatch, account])

  const getResourceRowClickAction = useCallback(function getResourceRowClickAction({ item }) {
    if (!item || !hasModels) {
      return undefined
    }

    function onResourceRowClick(ev) {
      if (ev.defaultPrevented) {
        return
      }
      if (item.shortType === "b") {
        openBasket(item)
        return
      }

      navigate(`/${account}/${item.shortType}/${item.alias}`)
    }

    return onResourceRowClick
  }, [hasModels, openBasket, navigate, account])

  return <div className="modules_resources-as-table">
    <RecentResources ref={recentRef} resources={recentResources} model={model} {...omit(props, ["recentResources", "resources"])} />
    <Paper className="resources-container" ref={containerRef}>
    <Table
      title="Resources"
      idField="id"
      addForm={false}
      data={resources}
      columns={columns}
      editForm={null}
      className="resources"
      filters={mappedFilters}
      filterForm={<FilterForm filters={filters} setFilter={(...args) => filterDispatch(...args)} isOpPro={isOpPro} favoritesScreen={favoritesScreen} />}
      rowsPerPage={ROWS_PER_PAGE}
      pagination
      favoritesScreen={favoritesScreen}
      getRowClickAction={getResourceRowClickAction}
    />
    </Paper>
    <Menu
      anchorEl={editAnchor}
      open={!!editAnchor}
      onClose={() => setEditAnchor(null)}
    >
      {menuItems}
    </Menu>
  </div>
}

Resources.propTypes = {
  dispatch: PropTypes.func.isRequired,
  account: PropTypes.string.isRequired,
  openDialog: PropTypes.func.isRequired,
  trackResourceEvent: PropTypes.func.isRequired,
  openSecurityProfile: PropTypes.func.isRequired,
  toggleFavorite: PropTypes.func.isRequired,
  resources: PropTypes.array.isRequired,
  model: PropTypes.string,
  models: PropTypes.object.isRequired,
  favoritesScreen: PropTypes.bool,
  setModel: PropTypes.func.isRequired,
  recentResources: PropTypes.array,
  intradayAccountEnabled: PropTypes.bool,
  isOpPro: PropTypes.bool.isRequired,
  accountDefaultCurrencyId: PropTypes.string,
  supportedCurrencies: PropTypes.arrayOf(CURRENCY),
}

export default Resources
